import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { parseValidDate } from '~/services/TimeService';

import type { RecordLike } from '../../shared/checklistUtils';
import {
  formatBooleanDataPointValue,
  formatOptionalNumber,
  getPackageDisplayName,
  isFiniteNumber,
  mapOpportunityDealsByPackageId,
  normalizeString,
  readBoolean,
  readNumber,
  readString,
} from '../../shared/checklistUtils';
import type { SetupRuleInput } from '../setup/types';

import { buildPolicyBibleNumberRow, buildPolicyBibleTextRow, findPolicyBibleCampaignRule } from './campaignPolicyBible';
import { buildCampaignRow, formatStringValue } from './campaignRow';
import { buildSuggestedValueRows } from './campaignSuggestedValue';
import type { CampaignChecklistRow } from './types';

const FLASH_CAMPAIGN_TYPES = ['FLASH - flexi nights', 'FLASH - standard'];
const MAX_BOOK_BY_DAYS_BEFORE_TRAVEL_TO = 62;
const MIN_BOOK_BY_DAYS_BEFORE_TRAVEL_TO = 25;
const EXTRA_NIGHT_MARGIN_FIELDS = ['extra_night_margin', 'extra_night_margin__c', 'Extra_Night_Margin__c'];

export function getOpportunityCampaignType({
  offer,
  opportunity,
}: {
  offer?: App.Offer;
  opportunity?: App.OpportunityPage;
}) {
  const opportunityInfo = (opportunity?.opportunity_info ?? {}) as unknown as RecordLike;

  return readString(opportunityInfo, ['campaign_type__c']) ?? offer?.campaign_type ?? offer?.type;
}

export function buildCampaignRows({
  offer,
  opportunity,
  opportunityDeals = [],
  packages,
  setupInput,
}: {
  offer?: App.Offer;
  opportunity?: App.OpportunityPage;
  opportunityDeals?: Array<OpportunityRelatedDeal>;
  packages?: Array<App.Package>;
  setupInput?: SetupRuleInput;
}): Array<CampaignChecklistRow> {
  const offerRecord = (offer ?? {}) as unknown as RecordLike;
  const functionalDetails = (opportunity?.functional_details ?? {}) as unknown as RecordLike;
  const keyDates = (opportunity?.key_dates ?? {}) as unknown as RecordLike;
  const keyCommercial = (opportunity?.key_commercial ?? {}) as unknown as RecordLike;
  const contract = (opportunity?.contract ?? {}) as unknown as RecordLike;

  const dateChangeAllowed = readBoolean(functionalDetails, ['date_change_allowed__c']);
  const daysBeforeCheckIn =
    readNumber(functionalDetails, ['days_before_check_in_changes_disallowed__c']) ??
    readNumber(offerRecord, ['days_before_check_in_changes_disallowed']);
  const numberOfDateChanges =
    readString(functionalDetails, ['number_of_date_changes__c']) ?? offer?.number_of_date_changes;
  const bookingCutoff =
    readNumber(functionalDetails, ['booking_data_cut_off_days__c']) ??
    readNumber(offerRecord, ['booking_min_lead_days']);
  const packageUpgradesAllowed =
    readBoolean(functionalDetails, ['package_upgrades_allowed__c']) ?? offer?.package_upgrades_allowed;
  const semSupport = deriveSemSupport(
    readBoolean(keyCommercial, ['no_sem_support_for_this_deal__c']) ?? readBoolean(offerRecord, ['sf_no_sem_support']),
  );
  const campaignType = getOpportunityCampaignType({ offer, opportunity });
  const paymentTermsModification =
    readString(contract, ['payment_terms_modification__c']) ??
    readString(contract, ['partner_b_payment_terms_modification__c']) ??
    readString(offerRecord, ['special_payment_terms']);
  const contractTermsModification = readString(contract, ['partner_b_contract_terms_modification__c']);
  const bookByDate = readString(keyDates, ['book_by__c']) ?? offer?.book_by_date;
  const travelToDate = readString(keyDates, ['travel_to__c']) ?? offer?.travel_to_date;
  const policyBibleRule = findPolicyBibleCampaignRule(setupInput);
  const packageRows = packages ?? [];
  const opportunityDealsByPackageId = mapOpportunityDealsByPackageId(opportunityDeals);
  const flexiPackages = packageRows.filter((offerPackage) => !!(offerPackage as unknown as RecordLike).flexible_nights);

  return [
    buildPolicyBibleNumberRow({
      field: 'Date change allowed',
      id: 'campaign-date-change-allowed',
      loadedValue: dateChangeAllowed ? daysBeforeCheckIn : undefined,
      policyBibleRule,
      source: 'Opportunity functional details: date_change_allowed__c',
      value: dateChangeAllowed ? formatOptionalNumber(daysBeforeCheckIn) : formatBooleanValue(dateChangeAllowed),
    }),
    buildPolicyBibleNumberRow({
      field: 'Days prior to check-in for date changes',
      id: 'campaign-date-change-prior-days',
      loadedValue: daysBeforeCheckIn,
      policyBibleRule,
      source: 'Opportunity functional details: days_before_check_in_changes_disallowed__c',
      value: formatOptionalNumber(daysBeforeCheckIn),
    }),
    buildPolicyBibleTextRow({
      field: 'Number of date changes',
      id: 'campaign-number-date-changes',
      loadedValue: numberOfDateChanges,
      policyBibleRule,
      source: 'Opportunity functional details: number_of_date_changes__c',
      value: formatStringValue(numberOfDateChanges),
    }),
    buildCampaignRow({
      action: bookingCutoff == null ? 'Populate booking data cut-off with 2.' : undefined,
      approvalNote: bookingCutoff == null ? 'Booking data cut-off needs to be populated before approval.' : undefined,
      approved: bookingCutoff != null,
      dataPoints: [
        { label: 'Loaded value', value: bookingCutoff == null ? 'Blank' : String(bookingCutoff) },
        { label: 'Expected value', value: '2' },
      ],
      evidence:
        bookingCutoff == null
          ? 'Booking data cut-off is blank and should be populated to 2.'
          : 'Booking data cut-off is populated.',
      field: 'Booking data cut-off',
      id: 'campaign-booking-cutoff',
      rule: 'If booking data cut-off is blank, populate = 2.',
      source: 'Opportunity functional details / offer booking_min_lead_days',
      value: bookingCutoff == null ? 'Blank' : String(bookingCutoff),
    }),
    buildCampaignRow({
      approved: packageUpgradesAllowed,
      dataPoints: [
        { label: 'Loaded value', value: formatBooleanValue(packageUpgradesAllowed) },
        { label: 'Expected value', value: 'Yes' },
      ],
      evidence: packageUpgradesAllowed
        ? 'Package upgrades are allowed.'
        : 'Package upgrades are not allowed or source value is missing.',
      field: 'Package upgrades allowed',
      id: 'campaign-package-upgrades',
      rule: 'Flag for approval when package upgrades are not allowed.',
      source: 'Opportunity functional details / offer package_upgrades_allowed',
      value: formatBooleanValue(packageUpgradesAllowed),
    }),
    buildCampaignRow({
      approved: semSupport !== 'Yes',
      dataPoints: [
        { label: 'Loaded value', value: semSupport },
        { label: 'Expected value', value: 'No' },
      ],
      evidence: semSupport === 'Yes' ? 'SEM support is enabled.' : 'SEM support is not enabled.',
      field: 'SEM support',
      id: 'campaign-sem-support',
      rule: 'Flag for approval only when SEM support is Yes.',
      source: 'Opportunity key commercial no_sem_support_for_this_deal__c / offer sf_no_sem_support',
      value: semSupport,
    }),
    buildCampaignRow({
      action:
        campaignType === 'FLASH - standard'
          ? 'Add note that flexi nights will not pull through to contract.'
          : undefined,
      approvalNote: campaignType === 'FLASH - standard' ? 'Flexi nights will not pull through to contract.' : undefined,
      approved: false,
      dataPoints: [
        { label: 'Loaded value', value: formatStringValue(campaignType) },
        { label: 'Expected value', value: 'FLASH - flexi nights or FLASH - standard' },
      ],
      evidence: FLASH_CAMPAIGN_TYPES.includes(campaignType || '')
        ? 'Campaign type is one of the expected Flash values.'
        : 'Campaign type is missing or outside the expected Flash values.',
      field: 'Campaign type',
      id: 'campaign-type',
      rule: 'Always flag campaign type for approval.',
      source: 'Opportunity info campaign_type__c / offer campaign_type',
      value: formatStringValue(campaignType),
    }),
    buildCampaignRow({
      approved: !normalizeString(String(paymentTermsModification ?? '')),
      dataPoints: [
        { label: 'Loaded value', value: formatStringValue(paymentTermsModification) },
        { label: 'Expected value', value: 'Blank' },
      ],
      evidence: paymentTermsModification
        ? 'Payment terms modification is populated.'
        : 'Payment terms modification is blank.',
      field: 'Payment Terms Modification',
      id: 'campaign-payment-terms-modification',
      rule: 'Auto-approve when the field is blank.',
      source: 'Opportunity contract payment terms modification fields',
      value: formatStringValue(paymentTermsModification),
    }),
    buildCampaignRow({
      approved: !normalizeString(contractTermsModification),
      dataPoints: [
        { label: 'Loaded value', value: formatStringValue(contractTermsModification) },
        { label: 'Expected value', value: 'Blank' },
      ],
      evidence: contractTermsModification
        ? 'Contract terms modification is populated.'
        : 'Contract terms modification is blank.',
      field: 'Contract Terms Modification',
      id: 'campaign-contract-terms-modification',
      rule: 'Auto-approve when the field is blank.',
      source: 'Opportunity contract partner_b_contract_terms_modification__c',
      value: formatStringValue(contractTermsModification),
    }),
    buildBookByRow({ bookByDate, travelToDate }),
    buildFlexiDealOptionsRow({ campaignType, flexiPackages }),
    buildFlexiNightMarginRow({ flexiPackages, opportunityDealsByPackageId }),
    ...buildSuggestedValueRows(offer?.packages, opportunityDeals),
  ];
}

function buildFlexiDealOptionsRow({
  campaignType,
  flexiPackages,
}: {
  campaignType?: string;
  flexiPackages: Array<App.Package>;
}): CampaignChecklistRow {
  const normalizedCampaignType = campaignType?.trim();
  const isFlexiCampaign = normalizedCampaignType ? /flexi/i.test(normalizedCampaignType) : undefined;
  const flexiPackageCount = flexiPackages.length;
  const approved = hasExpectedFlexiPackageCount(isFlexiCampaign, flexiPackageCount);

  return buildCampaignRow({
    approved,
    dataPoints: [
      { label: 'Campaign type', value: normalizedCampaignType || 'Not available' },
      { label: 'Flexi deal options', value: String(flexiPackageCount) },
      {
        label: 'Flexi option names',
        value: flexiPackages.map(getPackageDisplayName).join(', ') || 'None',
      },
    ],
    evidence: approved ? 'Flexi-night setup aligns with loaded deal options.' : 'Flexi-night setup needs review.',
    field: 'Flexi night + deal options',
    id: 'campaign-flexi-deal-options',
    rule: 'If flexi night, need at least 1 deal option with flexi night. If standard, no deal options with flexi night.',
    source: 'Opportunity info campaign_type__c / package flexible_nights',
    summary: approved ? 'Flexi-night setup aligns with loaded deal options.' : 'Flexi-night setup needs review.',
    value:
      typeof isFlexiCampaign === 'boolean'
        ? `${normalizedCampaignType}; ${flexiPackageCount} flexi deal option${flexiPackageCount === 1 ? '' : 's'}`
        : `Campaign type unavailable; ${flexiPackageCount} flexi deal option${flexiPackageCount === 1 ? '' : 's'}`,
  });
}

function hasExpectedFlexiPackageCount(isFlexiCampaign: boolean | undefined, flexiPackageCount: number) {
  if (isFlexiCampaign === undefined) {
    return false;
  }

  if (isFlexiCampaign) {
    return flexiPackageCount >= 1;
  }

  return flexiPackageCount === 0;
}

function buildFlexiNightMarginRow({
  flexiPackages,
  opportunityDealsByPackageId,
}: {
  flexiPackages: Array<App.Package>;
  opportunityDealsByPackageId: Map<string, OpportunityRelatedDeal>;
}): CampaignChecklistRow {
  const marginDetails = flexiPackages.map((offerPackage) => {
    const packageRecord = offerPackage as unknown as RecordLike;
    const packageId = readString(packageRecord, ['id_salesforce_external']);
    const opportunityDeal = packageId ? opportunityDealsByPackageId.get(packageId) : undefined;
    const margin = isFiniteNumber(opportunityDeal?.extra_night_margin__c)
      ? opportunityDeal.extra_night_margin__c
      : readNumber(packageRecord, EXTRA_NIGHT_MARGIN_FIELDS);

    return {
      margin,
      name: getPackageDisplayName(offerPackage),
    };
  });
  const hasFlexiPackages = marginDetails.length > 0;
  const hasAllMargins = marginDetails.every(({ margin }) => isFiniteNumber(margin));
  const hasOutOfRangeMargin = marginDetails.some(
    ({ margin }) => isFiniteNumber(margin) && (margin < 10 || margin > 50),
  );
  const approved = !hasFlexiPackages || (hasAllMargins && !hasOutOfRangeMargin);

  return buildCampaignRow({
    approved,
    dataPoints: hasFlexiPackages
      ? marginDetails.map(({ margin, name }) => ({
          label: name,
          value: isFiniteNumber(margin) ? `${margin}%` : 'Not available',
        }))
      : [{ label: 'Flexi deal options', value: 'None' }],
    evidence: approved ? 'Flexi night margins are within approved range.' : 'Flexi night margins need review.',
    field: 'Flexi night margin',
    id: 'campaign-flexi-night-margin',
    rule: 'If any flexi night margin is below 10% or above 50%, flag for approval; otherwise auto-approve.',
    source: 'Opportunity deal extra_night_margin__c / package extra_night_margin',
    summary: approved ? 'Flexi night margins are within approved range.' : 'Flexi night margins need review.',
    value: hasFlexiPackages
      ? marginDetails.map(({ margin, name }) => `${name}: ${isFiniteNumber(margin) ? `${margin}%` : 'N/A'}`).join('; ')
      : 'No flexi-night deal options',
  });
}

function buildBookByRow({
  bookByDate,
  travelToDate,
}: {
  bookByDate?: string | null;
  travelToDate?: string | null;
}): CampaignChecklistRow {
  const daysBeforeTravelTo = getDaysBetweenDates(bookByDate, travelToDate);
  const approved =
    daysBeforeTravelTo != null &&
    daysBeforeTravelTo >= MIN_BOOK_BY_DAYS_BEFORE_TRAVEL_TO &&
    daysBeforeTravelTo <= MAX_BOOK_BY_DAYS_BEFORE_TRAVEL_TO;

  return buildCampaignRow({
    approved,
    dataPoints: [
      { label: 'Book By date', value: formatStringValue(bookByDate) },
      { label: 'Travel To date', value: formatStringValue(travelToDate) },
      { label: 'Expected window', value: '25 to 62 days before Travel To' },
    ],
    evidence:
      daysBeforeTravelTo == null
        ? 'Book By or Travel To date is missing or invalid.'
        : `Book By is ${daysBeforeTravelTo} days before Travel To.`,
    field: 'Book By date',
    id: 'campaign-book-by-date',
    rule: 'Auto-approve when Book By is 25 days to 2 months before Travel To.',
    source: 'Opportunity key dates / offer book_by_date and travel_to_date',
    value: `${formatStringValue(bookByDate)} / ${formatStringValue(travelToDate)}`,
  });
}

function formatBooleanValue(value?: boolean | null) {
  return formatBooleanDataPointValue({
    dataAvailable: value != null,
    enabled: value ?? false,
    unavailableValue: 'Not available',
  });
}

function deriveSemSupport(noSemSupport?: boolean | null) {
  return noSemSupport ? 'No' : noSemSupport == null ? 'No' : 'Yes';
}

function getDaysBetweenDates(from?: string | null, to?: string | null) {
  const fromDate = parseValidDate(from);
  const toDate = parseValidDate(to);

  if (!fromDate || !toDate) {
    return undefined;
  }

  const millisecondsPerDay = 24 * 60 * 60 * 1000;

  return Math.round((toDate.getTime() - fromDate.getTime()) / millisecondsPerDay);
}
